/**
 * @typedef ActionType The action type determines what Method Name to use for the metrics publisher.
 * All metric types are to be concatenated with the page name (e.g., LearningPathwayPage.View).
 * @property {string} Click This indicates a user clicked on some feature on the given page.
 * @property {string} View This indicates a user viewed some feature on the given page.
 * @property {string} PageLoad This indicates a page finished loading its data; used to publish
 *   per-page latency and availability metrics (e.g., LearningPathwayPage.PageLoad).
 * The feature that was viewed and passed into props can even be the page itself.
 */
export enum ActionType {
  Click = 'Click',
  View = 'View',
  PageLoad = 'PageLoad',
}

/**
 * @typedef PagePerformanceMetric The metric names published under a page's PageLoad action
 * (e.g., LearningPathwayPage.PageLoad). These power the per-page seller-experience dashboard.
 * @property {string} Latency Time in milliseconds from component mount to data loaded
 *   (isLoading=false); published as a timer so it supports p50/p90/p99 stats.
 * @property {string} Success Counter incremented once per page load when the page loaded
 *   without error (isError=false). Availability % = Success / (Success + Fault) × 100.
 * @property {string} Fault Counter incremented once per page load when the page failed to
 *   load (isError=true). Fault rate % = Fault / (Success + Fault) × 100.
 */
export enum PagePerformanceMetric {
  Latency = 'Latency',
  Success = 'Success',
  Fault = 'Fault',
}

/**
 * @typedef FeatureName The names of all features we've attached metrics to. Values are used
 * within the publisher's method name (e.g., LearningPathwayPage.Interaction).
 * @property {string} SuRoot Captures the main Seller University application root.
 * @property {string} Website Captures the main Seller University website application.
 * @property {string} WidgetRoot Captures the widget container for embeddable Seller University content.
 * @property {string} RouteRedirect Captures route redirects and navigation events.
 * @property {string} HomePage Captures the Home page in the main application.
 * @property {string} SearchPage Captures the Search page in the main application.
 * @property {string} TopicPage Captures the Topic page in the main application.
 * @property {string} NotFoundPage Captures the 404 Not Found error page in the main application.
 * @property {string} RedirectHomeLink Captures interactions with links that redirect to the home page.
 * @property {string} LearningPathwayPage Captures the Learning Pathway page in the main application.
 * @property {string} WebinarPage Captures the Webinar page in the main application.
 * @property {string} WebinarButtonClick Captures clicks on all webinar buttons (register/join) in cards and modals.
 * @property {string} LearningPathwayWidget Captures the Learning Pathway page when accessed via widget embedding.
 * @property {string} LearningPathwaySection Captures interactions with Learning Pathway sections.
 * @property {string} LearningPathwaySectionCta Captures interactions with Learning Pathway section CTAs.
 * @property {string} ModuleCta Captures interactions with Learning Pathway module CTAs.
 * @property {string} ModuleDownloadBtn Captures interactions with Learning Pathway module download buttons.
 * @property {string} LearningModuleCard Captures interactions with a Learning Module Card.
 * @property {string} APITotalDuration Captures the total duration of API calls in milliseconds.
 * @property {string} APIErrorCount Captures the count of failed API calls.
 * @property {string} NavHomeLogoClick Captures clicks on the Seller University home logo in the nav header.
 * @property {string} NavLiveEventsClick Captures clicks on the Live Events button in the nav header.
 * @property {string} NavTopicsDropdownOpen Captures opens of the Topics dropdown in the nav header.
 * @property {string} NavSearchSubmit Captures search query submissions from the nav search bar.
 * @property {string} ModuleLanguageChange Captures language picker changes on a learning module's content view.
 * @property {string} ModuleMoreInfoLink Captures clicks on More Information links in a learning module's content view.
 */
export enum FeatureName {
  SuRoot = 'SuRoot',
  Website = 'Website',
  WidgetRoot = 'WidgetRoot',
  RouteRedirect = 'RouteRedirect',
  HomePage = 'HomePage',
  SearchPage = 'SearchPage',
  TopicPage = 'TopicPage',
  LearningModulePage = 'LearningModulePage',
  LearningModuleCardWidget = 'LearningModuleCardWidget',
  NotFoundPage = 'NotFoundPage',
  RedirectHomeLink = 'RedirectHomeLink',
  LearningPathwayPage = 'LearningPathwayPage',
  WebinarPage = 'WebinarPage',
  WebinarButtonClick = 'WebinarButtonClick',
  LearningPathwayWidget = 'LearningPathwayWidget',
  LearningPathwaySection = 'LearningPathwaySection',
  LearningPathwaySectionCta = 'LearningPathwaySectionCta',
  ModuleCta = 'ModuleCta',
  ModuleDownloadBtn = 'ModuleDownloadBtn',
  LearningModuleCard = 'LearningModuleCard',
  APITotalDuration = 'APITotalDuration',
  APIErrorCount = 'APIErrorCount',
  NavHomeLogoClick = 'NavHomeLogoClick',
  NavLiveEventsClick = 'NavLiveEventsClick',
  NavTopicsDropdownOpen = 'NavTopicsDropdownOpen',
  NavSearchSubmit = 'NavSearchSubmit',
  ModuleLanguageChange = 'ModuleLanguageChange',
  ModuleMoreInfoLink = 'ModuleMoreInfoLink',
  // Credential pilot assessment funnel counters (BRD §4).
  AssessmentSubmitted = 'AssessmentSubmitted',
  AssessmentPassed = 'AssessmentPassed',
  AssessmentError = 'AssessmentError',
  AssessmentStarted = 'AssessmentStarted', // seller picked a response and clicked Next for the first time in an attempt (once per attempt)
  AssessmentUnlocked = 'AssessmentUnlocked', // all required modules complete so the assessment is available; observed once per credential page mount
  AssessmentNotPassed = 'AssessmentNotPassed', // assessment submitted with a not-passed outcome (companion to AssessmentPassed)
  // Credential page counters (BRD §4): page-performance id, detail-view impression, awarded.
  CredentialPage = 'CredentialPage',
  CredentialDetail = 'CredentialDetail',
  CredentialEarned = 'CredentialEarned',
  // Credential pilot homepage ingress counters (BRD §4): banner and course-card entry points.
  CredentialBannerIngress = 'CredentialBannerIngress',
  CredentialCardIngress = 'CredentialCardIngress',
  // Credential pilot frontend funnel counters (BRD §4) between ingress and earned.
  CredentialCardImpression = 'CredentialCardImpression', // once per homepage credential card render for an eligible seller, carrying credential state. clientId is the SU surface id (e.g. SELLER_UNIVERSITY_CAMPUS), NOT the seller CID; seller CID is attributed downstream via the shared http_request_id to SellerUniversitySellerData
  CredentialBannerImpression = 'CredentialBannerImpression', // homepage credential banner shown, once per credential
  CredentialModuleStart = 'CredentialModuleStart', // a learning module opened from within the credential experience
  AssessmentIntroViewed = 'AssessmentIntroViewed', // assessment modal opened, before any question is answered
  CredentialCertificateViewed = 'CredentialCertificateViewed', // earned certificate panel presented
  CredentialCertificateDownloaded = 'CredentialCertificateDownloaded', // seller downloads the earned certificate
}
